import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import { generatePath, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import {
  So5FixtureEvent,
  So5LeaderboardType,
} from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { LabelL } from '@sorare/core/src/atoms/typography';
import { ExtraMessageWithBlurredBg } from '@sorare/core/src/components/onboarding/ExtraMessageWithBlurredBg';
import { HighlightOverlay } from '@sorare/core/src/components/onboarding/HighlightOverlay';
import {
  NBA_PLAY_EVENTTYPE_COMPOSE_LEADERBOARD,
  NBA_PLAY_EVENTTYPE_COMPOSE_LEADERBOARD_MANAGERTEAM_MANAGERTEAM,
} from '@sorare/core/src/constants/__generated__/routes';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { LIFECYCLE, useLifecycle } from '@sorare/core/src/hooks/useLifecycle';
import { useOnboardingHighlights } from '@sorare/core/src/hooks/useOnboardingHighlights';

import { useIsUserDailyPickupEligible } from 'hooks/useIsUserDailyPickupEligible';

import {
  NBADailyHoopsOnboardingQuery,
  NBADailyHoopsOnboardingQueryVariables,
} from './__generated__/index.graphql';

const StyledButton = styled(Button)`
  position: absolute;
  left: var(--unit);
  right: var(--unit);
  bottom: var(--quadruple-unit);
`;

const query = gql`
  query NBADailyHoopsOnboardingQuery {
    currentUser {
      slug
      nbaUserProfile {
        id
        onboardingSo5Fixture {
          slug
          so5Leaderboards {
            slug
            myManagerTeams {
              id
            }
            so5LeaderboardType
          }
        }
      }
    }
  }
` as TypedDocumentNode<
  NBADailyHoopsOnboardingQuery,
  NBADailyHoopsOnboardingQueryVariables
>;

export const DailyHoopsOnboarding = () => {
  const { data } = useQuery(query);
  const navigate = useNavigate();
  const isTabletAndAbove = useIsTabletAndAbove();
  const onboardingHighlights = useOnboardingHighlights();
  const onboardingFixture =
    data?.currentUser?.nbaUserProfile?.onboardingSo5Fixture;
  const dailyPickupLeaderboard = onboardingFixture?.so5Leaderboards.find(
    leaderboard =>
      [
        So5LeaderboardType.NBA_COMMON_DAILY_PICKUP,
        So5LeaderboardType.NBA_COMMON_DAILY_SPECIAL,
      ].includesValue(leaderboard.so5LeaderboardType)
  );
  const { lifecycle, update: updateLifecycle } = useLifecycle();
  const { isDailyPickupEligible, loading } = useIsUserDailyPickupEligible();

  if (
    !onboardingFixture ||
    !onboardingHighlights ||
    lifecycle?.sawDailyHoopsOnboarding
  ) {
    return null;
  }
  if (!loading && !isDailyPickupEligible) {
    updateLifecycle(LIFECYCLE.sawDailyHoopsOnboarding, true);
  }

  return (
    <HighlightOverlay
      stayOpen
      key="DailyHoopsOnboarding"
      disableBackdropClose
      onClose={() => {
        const managerId = dailyPickupLeaderboard?.myManagerTeams[0]?.id;
        updateLifecycle(LIFECYCLE.sawDailyHoopsOnboarding, true);
        navigate(
          generatePath(
            managerId
              ? NBA_PLAY_EVENTTYPE_COMPOSE_LEADERBOARD_MANAGERTEAM_MANAGERTEAM
              : NBA_PLAY_EVENTTYPE_COMPOSE_LEADERBOARD,
            {
              leaderboard: dailyPickupLeaderboard?.slug ?? '',
              eventType: So5FixtureEvent.DAILY.toLowerCase(),
              managerTeam: idFromObject(
                dailyPickupLeaderboard?.myManagerTeams[0]?.id ?? ''
              ),
            }
          )
        );
      }}
      highlightNames={['FixtureSelectorContainer']}
      extras={[
        {
          nextTo: 'FixtureSelectorContainer',
          render: ({ onClose, ...props }) => (
            <>
              <ExtraMessageWithBlurredBg
                align="center"
                positionProps={props}
                fullWidth={!isTabletAndAbove}
                anchor={isTabletAndAbove ? 'top' : 'bottom'}
              >
                <LabelL>
                  <FormattedMessage
                    id="DailyHoopsOnboarding.FixtureSelector"
                    defaultMessage="Competitions run every day"
                  />
                </LabelL>
              </ExtraMessageWithBlurredBg>
              <StyledButton
                fullWidth
                size="large"
                color="secondary"
                onClick={onClose}
                style={{
                  top: isTabletAndAbove
                    ? `calc(${props.bottom}px + var(--double-unit))`
                    : '',
                  width: isTabletAndAbove ? '340px' : props.right - props.left,
                  left: isTabletAndAbove
                    ? `calc(${props.left}px + (${props.right - props.left}px - 340px) / 2)`
                    : props.left,
                }}
              >
                <FormattedMessage
                  id="DailyHoopsOnboarding.composeLineup"
                  defaultMessage="Compose your first lineup"
                />
              </StyledButton>
            </>
          ),
        },
      ]}
    />
  );
};
